import { Button, Typography, Box } from '@mui/material';
import { useSnackbar } from 'src/components/snackbar';
import { saveAs } from 'file-saver';
import { useSWRConfig } from 'swr';

import axiosInstance, { endpoints } from 'src/utils/axios';
import { getDateForFileExport, todayInRome } from 'src/utils/get-date-worlds-format';

import { ILogisticsStats } from 'src/types/misc';
import { useLocales } from 'src/locales';
import CustomTable from '../list/custom-table';

export default function LogisticsSection({
  blockedCount,
  ordersToBeShipped,
  ordersShippedToday,
  ordersShippedYesterday,
  ordersToBeShippedTomorrow,
}: ILogisticsStats) {
  const { t } = useLocales('operativaDashboard');
  const { enqueueSnackbar } = useSnackbar();
  const { mutate } = useSWRConfig();

  // FIXME: we should not be writing axios calls in the component like this, do not copy this code futher
  const downloadCSV = async () => {
    try {
      const params = { date: todayInRome() };
      const response = await axiosInstance(`${endpoints.admin.overview.logisticsCsv}`, {
        params,
        responseType: 'blob',
      });

      const dateStr = getDateForFileExport();
      saveAs(new Blob([response.data]), `logistics ${dateStr}.csv`);
    } catch (error) {
      console.error('Error downloading the CSV');

      let message = t('csvDownloadFailed');

      // Handle blob error response
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json.message || message;
        } catch (err) {
          console.error('Error parsing error response', err);
        }
      } else {
        message = error?.response?.data?.message || error.message;
      }

      enqueueSnackbar(message, { variant: 'error' });

      mutate([
        `${endpoints.admin.overview.logistics}`,
        {
          params: { currentDate: todayInRome() },
        },
      ]);
    }
  };

  // FIXME: we should not be writing axios calls in the component like this, do not copy this code futher
  const sendToLogistic = async () => {
    try {
      const params = { date: todayInRome() };

      await axiosInstance(`${endpoints.admin.overview.logisticsCsvEmail}`, {
        params,
      });

      enqueueSnackbar(t('email_sent_successfully'), { variant: 'success' });

      mutate([
        `${endpoints.admin.overview.logistics}`,
        {
          params: { currentDate: todayInRome() },
        },
      ]);
    } catch (error) {
      console.error('Error sending to logistic', error.response);
      const message = error?.response?.data?.message || error.message;
      enqueueSnackbar(message, { variant: 'error' });

      mutate([
        `${endpoints.admin.overview.logistics}`,
        {
          params: { currentDate: todayInRome() },
        },
      ]);
    }
  };
  const downloadDachserFercamCSV = async () => {
    try {
      const params = { date: todayInRome() };
      const response = await axiosInstance(`${endpoints.admin.overview.dachserFercamCsv}`, {
        params,
        responseType: 'blob',
      });

      const dateStr = getDateForFileExport();
      saveAs(new Blob([response.data]), `logistics-dachser-fercam ${dateStr}.csv`);
    } catch (error) {
      console.error('Error downloading the Dachser & Fercam CSV');

      let message = t('csvDownloadFailed');

      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          message = json.message || message;
        } catch (err) {
          console.error('Error parsing error response', err);
        }
      } else {
        message = error?.response?.data?.message || error.message;
      }

      enqueueSnackbar(message, { variant: 'error' });

      mutate([
        `${endpoints.admin.overview.logistics}`,
        {
          params: { currentDate: todayInRome() },
        },
      ]);
    }
  };

  // Determine if there is data to show in the table and conditionally show the button

  const isDataAvailableForCSV = ordersShippedToday && ordersShippedToday.length > 0;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h5">{t('ordini_da_spedire')}</Typography>
        {/* Orders with no usable dispatch date. Absent on a backend that predates NAT-1713, and
            a zero is worth stating: it is the difference between "nothing is stuck" and
            "nobody is looking". */}
        {blockedCount !== undefined ? (
          <Typography
            variant="subtitle2"
            sx={{ color: blockedCount > 0 ? 'error.main' : 'text.secondary' }}
          >
            {t('ordini_bloccati')}: {blockedCount}
          </Typography>
        ) : null}
      </Box>
      <CustomTable data={ordersToBeShipped} />
      <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
        {t('ordini_spediti_domani')}
      </Typography>
      <CustomTable data={ordersToBeShippedTomorrow} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
          justifyContent: { xs: 'center', sm: 'space-between' },
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
          {t('ordini_spediti_oggi')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
          }}
        >
          {isDataAvailableForCSV && (
            <Button onClick={downloadCSV} variant="contained" sx={{ height: '18%', mb: 2, mt: 4 }}>
              {`${t('download')} CSV BRT`}
            </Button>
          )}
          {isDataAvailableForCSV && (
            <Button
              onClick={downloadDachserFercamCSV}
              variant="contained"
              sx={{ height: '18%', mb: 2, mt: 4 }}
            >
              {`${t('download')} CSV D&F`}
            </Button>
          )}
          {isDataAvailableForCSV && (
            <Button
              onClick={sendToLogistic}
              variant="contained"
              sx={{ height: '18%', mb: 2, mt: 4 }}
            >
              {t('send_to_logistic')}
            </Button>
          )}
        </Box>
      </Box>
      <CustomTable data={ordersShippedToday} />{' '}
      {/* Add margin top to CustomTable for additional space */}
      <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
        {t('ordini_spediti_ieri')}
      </Typography>
      <CustomTable data={ordersShippedYesterday} />
    </Box>
  );
}
